import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { ArrowLeft, Tag, Check, X, Save, Loader2, Percent } from 'lucide-react';

interface Promotion {
  id: string;
  name: string;
  mechanic: string;
  start_date: string;
  end_date: string;
}

interface PromotionCheckItem {
  promotion_id: string;
  is_present: boolean;
  is_price_correct?: boolean;
  is_material_present?: boolean;
  notes?: string;
}

const MECHANIC_LABELS: Record<string, string> = {
  DISCOUNT: 'Desconto',
  BUY_X_PAY_Y: 'Leve X Pague Y',
  GIFT: 'Brinde',
  BUNDLE: 'Combo',
  CASHBACK: 'Cashback',
  OTHER: 'Outro',
};

export default function PromotionCheck({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [checks, setChecks] = useState<Record<string, PromotionCheckItem>>({});
  const [saved, setSaved] = useState(false);

  const { data: promotions, isLoading } = useQuery<Promotion[]>({
    queryKey: ['promotions-active'],
    queryFn: async () => {
      try {
        const response = await api.get('/promotions?status=active');
        return response.data.data;
      } catch {
        return [];
      }
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const items = Object.entries(checks).map(([promotion_id, check]) => ({
        visit_id: visitId,
        store_id: storeId,
        ...check,
        promotion_id,
      }));
      for (const item of items) {
        await api.post('/promotions/checks', item);
      }
    },
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
      setTimeout(() => setLocation(`/visit/${storeId}`), 1500);
    },
  });

  const togglePresent = (promoId: string, isPresent: boolean) => {
    setChecks((prev) => ({
      ...prev,
      [promoId]: {
        ...(prev[promoId] || {}),
        promotion_id: promoId,
        is_present: isPresent,
      },
    }));
  };

  const toggleExtra = (promoId: string, field: 'is_price_correct' | 'is_material_present', value: boolean) => {
    setChecks((prev) => ({
      ...prev,
      [promoId]: {
        ...(prev[promoId] || {}),
        promotion_id: promoId,
        is_present: prev[promoId]?.is_present ?? true,
        [field]: value,
      },
    }));
  };

  const checkedCount = Object.values(checks).filter((c) => c.is_present).length;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button onClick={() => setLocation(`/visit/${storeId}`)} className="p-2 -ml-2 rounded-full hover:bg-white/20">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Promoções</h1>
            <p className="text-sm text-white/80">Verificar promoções ativas</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{checkedCount}/{promotions?.length || 0}</p>
            <p className="text-xs text-white/70">executadas</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 space-y-3">
        {saved && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800 font-medium">Verificação salva!</span>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : !promotions?.length ? (
          <div className="text-center py-12">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhuma promoção ativa no momento.</p>
          </div>
        ) : (
          promotions.map((promo) => {
            const check = checks[promo.id];
            const isPresent = check?.is_present === true;
            return (
              <div key={promo.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-purple-50">
                    <Percent className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{promo.name}</p>
                    <p className="text-xs text-gray-500">{MECHANIC_LABELS[promo.mechanic] || promo.mechanic}</p>
                  </div>
                </div>

                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => togglePresent(promo.id, true)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isPresent
                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                    }`}
                  >
                    <Check className="h-4 w-4" />
                    Executada
                  </button>
                  <button
                    onClick={() => togglePresent(promo.id, false)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                      check?.is_present === false
                        ? 'bg-red-100 text-red-700 border-2 border-red-300'
                        : 'bg-gray-50 text-gray-600 border border-gray-200'
                    }`}
                  >
                    <X className="h-4 w-4" />
                    Ausente
                  </button>
                </div>

                {isPresent && (
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => toggleExtra(promo.id, 'is_price_correct', !check?.is_price_correct)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        check?.is_price_correct
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-gray-50 text-gray-500 border border-gray-200'
                      }`}
                    >
                      Preço correto
                    </button>
                    <button
                      onClick={() => toggleExtra(promo.id, 'is_material_present', !check?.is_material_present)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        check?.is_material_present
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-gray-50 text-gray-500 border border-gray-200'
                      }`}
                    >
                      Material presente
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </main>

      {Object.keys(checks).length > 0 && (
        <div className="p-4 bg-white border-t border-gray-200">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || saved}
            className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            {saveMutation.isPending ? 'Salvando...' : 'Salvar Verificação'}
          </button>
        </div>
      )}
    </div>
  );
}
